// Standard page layout: header + centered content container with padding.
// Used by Dashboard, Sessions, Account, About, etc.

import React from 'react';
import { Box, Container, CssBaseline, ThemeProvider } from '@mui/material';
import AppHeader from './AppHeader';
import DropPageBrandBar from './DropPageBrandBar';
import { theme } from '../theme';

interface PageLayoutProps {
  children: React.ReactNode;
  maxWidth?: 'sm' | 'md' | 'lg' | 'xl' | false;
  fullBleed?: boolean; // if true, skip the Container (for edge-to-edge pages like AI)
  showNav?: boolean;
}

const DROP_PATH_PREFIXES = ['/shared/', '/peak/', '/dropped/', '/replay/'];

function isDropPage(pathname: string): boolean {
  return DROP_PATH_PREFIXES.some((p) => pathname.startsWith(p));
}

export default function PageLayout({
  children,
  maxWidth = 'lg',
  fullBleed = false,
  showNav = true,
}: PageLayoutProps) {
  // Wave 20.47d — public drop pages always show the aero.chat
  // brand bar (logo + Join CTA). Route-driven, so any current or
  // future drop route picks it up.
  const showDropBar =
    typeof window !== 'undefined' && isDropPage(window.location.pathname);
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
        <AppHeader showNav={showNav} />
        {showDropBar && <DropPageBrandBar />}
        {fullBleed ? (
          // Wave 20.44 — `position: relative` so fullBleed children
          // using `position: absolute; inset: 0` are contained inside
          // main (i.e. sit BELOW the sticky AppHeader) instead of
          // escaping to the viewport and overlaying the header.
          <Box component="main" sx={{ flexGrow: 1, position: 'relative' }}>
            {children}
          </Box>
        ) : (
          <Container
            component="main"
            maxWidth={maxWidth}
            sx={{ py: { xs: 4, md: 6 }, flexGrow: 1 }}
          >
            {children}
          </Container>
        )}
      </Box>
    </ThemeProvider>
  );
}
